/**
 * "Connect MCP": the one way into adding a custom MCP server, in the top-right
 * corner of both places a firm looks for its connections.
 *
 * - Compliance → Integrations (`defaultScope="organization"`) adds servers for
 *   the whole organization, so the button is an admin's; anyone else sees no
 *   button at all.
 * - Plugins & Skills (`defaultScope="personal"`) shows it to every member:
 *   "Only me" is preselected, and an admin may choose "Everyone in <org>".
 *
 * @module components/apps/integrations/ConnectMcpButton
 */
import { useNavigate } from "@tanstack/react-router";
import { WebhookIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useOrgProfile } from "../../../state/orgProfile";
import { useCustomMcpServers, type CustomMcpScope } from "../../../state/customMcpServers";
import { Button } from "../../ui/button";
import { CustomMcpServerDialog } from "./CustomMcpServerDialog";
import {
  connectionFromForm,
  customMcpScopeOptions,
  type CustomMcpForm,
} from "./customMcpServerContent";

/**
 * Open a server's page: personal servers live under Plugins, the
 * organization's under Compliance → Integrations. Two literal routes rather
 * than one computed `to`, so each navigation is typed against its own route.
 */
export function openCustomMcpServer(
  navigate: ReturnType<typeof useNavigate>,
  target: { readonly scope: CustomMcpScope; readonly org: string; readonly serverId: string },
): void {
  const params = { org: target.org, serverId: target.serverId };
  if (target.scope === "personal") {
    void navigate({ to: "/$org/connections/mcp/$serverId", params });
  } else {
    void navigate({ to: "/$org/apps/custom/$serverId", params });
  }
}

export function ConnectMcpButton({
  organizationId,
  orgSegment,
  defaultScope = "organization",
}: {
  readonly organizationId: string | null;
  /** The org segment the new server's page opens under. */
  readonly orgSegment: string | null;
  /** Who a new server is for unless the person picks otherwise (only admins can). */
  readonly defaultScope?: CustomMcpScope;
}) {
  const navigate = useNavigate();
  const { canManage, profile } = useOrgProfile(organizationId);
  const { create } = useCustomMcpServers(organizationId);
  const [open, setOpen] = useState(false);
  const scopeOptions = useMemo(
    () =>
      customMcpScopeOptions({
        defaultScope,
        canManageOrganization: canManage,
        organizationName: profile?.name ?? null,
      }),
    [canManage, defaultScope, profile?.name],
  );
  const submit = useCallback(
    (form: CustomMcpForm) =>
      create({ name: form.name, scope: form.scope, ...connectionFromForm(form) }),
    [create],
  );

  if (scopeOptions.length === 0 || organizationId === null || orgSegment === null) return null;

  return (
    <>
      <Button size="sm" className="cursor-pointer rounded-full" onClick={() => setOpen(true)}>
        <WebhookIcon className="size-4" />
        Connect MCP
      </Button>
      <CustomMcpServerDialog
        open={open}
        onOpenChange={setOpen}
        scopeOptions={scopeOptions}
        onSubmit={submit}
        onConnected={(server) => {
          if (server === null) return;
          openCustomMcpServer(navigate, {
            scope: server.scope,
            org: orgSegment,
            serverId: server.id,
          });
        }}
      />
    </>
  );
}
